import type { MarkdownIt, StateInline, Token } from "markdown-it";

// Imported Jira {color:x}...{/color} markers become a color span only when the color is a
// literal hex value or one of these names. Everything the converter emits is a valid CSS
// color; validating here means an untrusted import can never inject anything but a color into
// the style attribute, so the renderer keeps HTML disabled and needs no sanitizer.
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const NAMED = new Set([
    "black",
    "silver",
    "gray",
    "grey",
    "white",
    "maroon",
    "red",
    "purple",
    "fuchsia",
    "green",
    "lime",
    "olive",
    "yellow",
    "navy",
    "blue",
    "teal",
    "aqua",
    "orange",
]);

function isValidColor(color: string): boolean {
    return HEX.test(color) || NAMED.has(color.toLowerCase());
}

const OPEN = /^\{color:([^}]+)\}/;
const CLOSE = "{/color}";

export function colorSpans(md: MarkdownIt) {
    function createColorSpan(state: StateInline, silent: boolean): boolean {
        if (state.src[state.pos] !== "{") {
            return false;
        }

        const open = OPEN.exec(state.src.slice(state.pos));
        if (!open) {
            return false;
        }

        const afterOpen = state.pos + open[0].length;
        const closeIndex = state.src.indexOf(CLOSE, afterOpen);
        if (closeIndex < 0) {
            return false;
        }

        if (!silent) {
            const color = (open[1] ?? "").trim();
            const inner: Token[] = [];
            state.md.inline.parse(
                state.src.slice(afterOpen, closeIndex),
                state.md,
                state.env,
                inner
            );

            if (isValidColor(color)) {
                const openToken = state.push("color_open", "span", 1);
                openToken.attrSet("style", `color:${color}`);
                for (const token of inner) {
                    state.tokens.push(token);
                }
                state.push("color_close", "span", -1);
            } else {
                // Flush any pending text before splicing the inner tokens in, then drop the
                // unrecognized color wrapper and keep the escaped inner content.
                state.push("text", "", 0);
                for (const token of inner) {
                    state.tokens.push(token);
                }
            }
        }

        state.pos = closeIndex + CLOSE.length;
        return true;
    }

    md.inline.ruler.before("emphasis", "colorSpans", createColorSpan);
}
